import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    IconButton,
    InputLabel,
    Paper,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { shallow } from 'zustand/shallow';

import { FormTextField } from '../custom-form-text-field';
import { InlineJsonEditor } from '../inline-json-editor';

import { BaseNodeForm } from './base-node-form';

import { CustomInternalModuleBotModuleData, NodeType } from '@/domain/bot';
import { NodeFormProps } from '@/domain/node-form-props';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { useTelmaPortalStore } from '@/store/telma-portal-store';
import {
    getHandlesFromEdges,
    sortHandles,
} from '@/utils/reactflow/get-handles-from-edges';
import { useSaveBotVersionEnabled } from 'utils/hooks/use-save-bot-version-enabled';

type CustomInternalModuleBotModuleDataParams =
    CustomInternalModuleBotModuleData['params'] &
        CustomInternalModuleBotModuleData['source'];

export const botCustomInternalModuleNodeSchema = yup.object({
    typeOverride: yup.string().max(500, 'too_long'),
    params: yup.mixed().test('is-valid-json', function (value) {
        if (typeof value === 'object' && !Array.isArray(value)) {
            return true;
        }

        if (typeof value === 'string') {
            try {
                JSON.parse(value);
                return true;
            } catch (e) {
                return this.createError({
                    message: 'errors.invalid_json',
                });
            }
        }

        return this.createError({
            message: 'errors.invalid_json',
        });
    }),
    source: yup.object({
        path: yup.string().max(500, 'too_long'),
        unpack_params: yup.bool(),
    }),
});

export const CustomInternalModuleNode: FC<NodeFormProps> = ({ nodeIndex }) => {
    const [setNode, node, edges, setEdges] = useTelmaPortalStore(
        (state) => [
            state.setNode,
            state.editingBot.nodesReactFlow[nodeIndex],
            state.editingBot.edgesReactFlow,
            state.setEdges,
        ],
        shallow,
    );
    const theme = useTheme();

    const [nodeHandles, setNodeHandles] = useTelmaPortalStore(
        (state) => [
            node?.id ? state.customModuleNodeHandles[node.id] || [] : [],
            state.setCustomModuleNodeHandles,
        ],
        shallow,
    );

    const getNodeHandles = useCallback(() => {
        const edgeHandles = getHandlesFromEdges(node.id, edges);
        return edgeHandles.length > 0 ? edgeHandles : ['0'];
    }, [edges, node.id]);

    useEffect(() => {
        if (node?.id && (!nodeHandles || nodeHandles.length === 0)) {
            const initialHandles = getNodeHandles();
            setNodeHandles(node.id, initialHandles);
        }
    }, [node?.id, nodeHandles, getNodeHandles, setNodeHandles]);

    const saveEnabled = useSaveBotVersionEnabled();
    const { t } = useTranslation();

    const { isCustomModuleEditingEnabled } = useUserPermissions();
    const nodeData = node?.data as CustomInternalModuleBotModuleData;

    const { handleSubmit, control, setValue, getValues } =
        useForm<CustomInternalModuleBotModuleDataParams>({
            resolver: yupResolver(botCustomInternalModuleNodeSchema),
            defaultValues: {
                params: nodeData?.params,
                source: {
                    ...nodeData?.source,
                    path: nodeData?.source?.path || '',
                    unpack_params: true,
                },
                typeOverride: nodeData?.typeOverride || '',
            },
        });

    const botNodeSubmit: SubmitHandler<
        CustomInternalModuleBotModuleDataParams
    > = (formData) => {
        setNode(
            nodeIndex,
            formData.params,
            {
                path: formData.source.path,
                unpack_params: true,
            },
            formData.typeOverride,
        );
    };

    const addHandle = () => {
        const existingHandles = [...nodeHandles];
        let maxHandleId = -1;
        existingHandles.forEach((handle) => {
            const handleNum = parseInt(handle, 10);
            if (!isNaN(handleNum) && handleNum > maxHandleId) {
                maxHandleId = handleNum;
            }
        });

        const newHandleId = (maxHandleId + 1).toString();
        const newHandles = [...existingHandles, newHandleId];
        sortHandles(newHandles);
        setNodeHandles(node.id, newHandles);
    };

    const removeHandle = () => {
        const handleToRemove = nodeHandles[nodeHandles.length - 1];
        const updatedEdges = edges.filter((edge) => {
            if (edge.source === node.id) {
                const handleId = edge.sourceHandle?.split('_').pop();
                return handleId !== handleToRemove;
            }
            return true;
        });

        setEdges(updatedEdges);
        setNodeHandles(node.id, nodeHandles.slice(0, -1));
    };

    return (
        <BaseNodeForm nodeId={node.id} nodeTitle={node.type}>
            <form onBlur={handleSubmit(botNodeSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Paper
                        variant='outlined'
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: theme.palette.secondary.veryLight,
                            backgroundColor: 'inherit',
                        }}
                    >
                        <Box sx={{ mb: 2 }}>
                            <InputLabel className='input-label-card'>
                                {t('custom-module.type_override')}
                            </InputLabel>
                            <Controller
                                name='typeOverride'
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormTextField
                                        {...field}
                                        fieldError={error}
                                        fullWidth
                                        placeholder={t(
                                            'custom-module.type_override_placeholder',
                                        )}
                                        disabled={!saveEnabled}
                                    />
                                )}
                            />
                        </Box>

                        <Box>
                            <InputLabel className='input-label-card'>
                                {t('custom-module.source_path')}
                            </InputLabel>
                            <Controller
                                name='source.path'
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormTextField
                                        {...field}
                                        fieldError={error}
                                        fullWidth
                                        placeholder={t(
                                            'custom-module.source_path_placeholder',
                                        )}
                                        disabled={!saveEnabled}
                                    />
                                )}
                            />
                        </Box>
                    </Paper>

                    <Paper
                        variant='outlined'
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: theme.palette.secondary.veryLight,
                            backgroundColor: 'inherit',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'inherit',
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant='cardTitle'
                                sx={{ flexGrow: 1 }}
                            >
                                {t('custom-module.adjust_handles')}
                            </Typography>
                            <Tooltip title={t('custom-module.add_handle')}>
                                <IconButton
                                    size='small'
                                    onClick={addHandle}
                                    disabled={!saveEnabled}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('custom-module.remove_handle')}>
                                <span>
                                    <IconButton
                                        size='small'
                                        onClick={removeHandle}
                                        disabled={
                                            !saveEnabled ||
                                            nodeHandles.length <= 1
                                        }
                                        color='secondary'
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <div style={{ display: 'flex' }}>
                            <InfoIcon color='primary' />
                            <Typography
                                variant='body2'
                                color='primary'
                                sx={{ mb: 2, ml: 1, fontStyle: 'italic' }}
                            >
                                {t('custom-module.handles_info')}
                            </Typography>
                        </div>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {nodeHandles.map((handle, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        backgroundColor: (theme) =>
                                            theme.palette.background.default,
                                        position: 'relative',
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        sx={{ fontWeight: 'medium' }}
                                    >
                                        {handle}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: 'black',
                                            border: '2px solid #666',
                                            position: 'absolute',
                                            bottom: -6,
                                            left: '50%',
                                            top: 'auto',
                                            transform: 'translateX(-50%)',
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    <Paper
                        variant='outlined'
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: theme.palette.secondary.veryLight,
                            backgroundColor: 'inherit',
                        }}
                    >
                        <Typography variant='cardTitle'>
                            {t('custom-module.pass_parameters')}
                        </Typography>
                        <br />
                        <br />
                        <Controller
                            name='params'
                            control={control}
                            render={({
                                field: { value, onChange, onBlur },
                                fieldState: { error },
                            }) => (
                                <>
                                    {error && (
                                        <Typography
                                            variant='caption'
                                            color='error'
                                            sx={{
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            {t(`${error.message}`)}
                                        </Typography>
                                    )}
                                    <InlineJsonEditor
                                        value={value}
                                        onChange={(insertedParams) => {
                                            onChange(insertedParams);
                                            const customModuleData =
                                                node.data as CustomInternalModuleBotModuleData;

                                            setNode(
                                                nodeIndex,
                                                insertedParams,
                                                customModuleData.source,
                                                customModuleData.typeOverride as NodeType,
                                            );
                                        }}
                                        readOnly={!isCustomModuleEditingEnabled}
                                        onBlur={onBlur}
                                        height='250px'
                                    />
                                </>
                            )}
                        />
                    </Paper>
                </Box>
            </form>
        </BaseNodeForm>
    );
};
